import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ROLE_LABELS } from "@/auth";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Copy, Sparkles, Building2, Heart, Rocket, Flag, Lock } from "lucide-react";

export default function SubaccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "", password: "", name: "", role: "coord_municipal",
    region_type: "", region_name: "", municipio_clave: "",
  });
  const [municipios, setMunicipios] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/superadmin/subaccounts/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar la subcuenta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (data?.subaccount?.entidad_clave) {
      api.get("/catalog/municipios", { params: { entidad_clave: data.subaccount.entidad_clave } })
        .then((r) => setMunicipios(r.data));
    }
  }, [data?.subaccount?.entidad_clave]);

  if (loading) return <div className="min-h-screen bg-slate-50"><AppHeader /><div className="p-12 text-center">Cargando…</div></div>;
  if (!data) return null;

  const { subaccount: s, users } = data;
  const publicLink = `${window.location.origin}/r/${s.slug}`; // placeholder; real recruitment uses link codes

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newUser,
        subaccount_id: s.id,
        municipio_clave: newUser.municipio_clave ? parseInt(newUser.municipio_clave, 10) : null,
        region_type: newUser.region_type || null,
        region_name: newUser.region_name || null,
      };
      await api.post("/users", payload);
      toast.success("Usuario creado");
      setShowDialog(false);
      setNewUser({ email: "", password: "", name: "", role: "coord_municipal", region_type: "", region_name: "", municipio_clave: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error creando usuario");
    }
  };

  const removeUser = async (uid) => {
    if (!confirm("¿Eliminar este usuario?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error eliminando");
    }
  };

  const enabled = s.enabled_roles || [];
  const needsRegion = ["coord_regional", "coord_colonia"].includes(newUser.role);
  const needsMunicipio = newUser.role === "coord_municipal" || ((newUser.role === "coord_colonia" || newUser.role === "coord_regional") && s.scope === "estatal");

  const toggleModule = async (key, value) => {
    const currentModules = s.modules || {
      viabilidad: true, estructura: true, fidelizacion: false, dispersion: false, dia_d: false,
    };
    const nextModules = { ...currentModules, [key]: value };
    try {
      await api.patch(`/superadmin/subaccounts/${s.id}`, { modules: nextModules });
      toast.success(value ? "Módulo habilitado" : "Módulo deshabilitado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error actualizando módulo");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subaccount-detail-page">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <button onClick={() => nav("/super-admin")} className="text-sm tracking-label text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="back-superadmin-detail">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="mt-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div>
            <span className="tracking-label text-blue-700">Subcuenta · {scopeTypeLabel(s)}</span>
            <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-slate-900">{s.name}</h1>
            <p className="text-sm text-slate-600 mt-1">
              Candidato/a: <strong>{s.candidate_name}</strong>
            </p>
            <div className="mt-3 text-sm text-slate-600">
              {s.entidad_nombre}{s.municipio_nombre ? ` · ${s.municipio_nombre}` : ""}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {enabled.map((r) => (
                <Badge key={r} variant="outline" className="uppercase tracking-wider text-[10px]">{ROLE_LABELS[r] || r}</Badge>
              ))}
            </div>
          </div>
          <div className="surface-card p-5 max-w-md w-full">
            <div className="tracking-label text-slate-500">Slug de la subcuenta</div>
            <div className="mt-1 font-display font-bold text-lg text-slate-900">{s.slug}</div>
            <div className="mt-3 text-xs text-slate-500">
              Los enlaces de reclutamiento se generan dentro del panel del Admin Candidato y siguen el patrón{" "}
              <code className="bg-slate-100 px-1 rounded">/r/&lt;código&gt;</code>.
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Input value={publicLink} readOnly className="text-xs" data-testid="public-base-link" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicLink); toast.success("Copiado"); }} data-testid="copy-public-link">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* NOCTUA 360 Modules toggle */}
        <ModulesToggleSection subaccount={s} onToggle={toggleModule} />

        {/* Users */}
        <section className="surface-card mt-10 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-display font-bold text-lg text-slate-900">Usuarios y roles activos</h2>
              <p className="text-xs text-slate-500 mt-0.5">{users.length} usuarios en esta subcuenta</p>
            </div>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-user-button">
                  <Plus className="w-4 h-4 mr-1.5" /> Agregar usuario
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Crear usuario</DialogTitle></DialogHeader>
                <form onSubmit={createUser} className="space-y-3" data-testid="create-user-form">
                  <div>
                    <Label>Rol</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger data-testid="select-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {enabled.filter((r) => r !== "admin_candidato").map((r) => (
                          <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nombre</Label>
                    <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required data-testid="input-new-user-name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required data-testid="input-new-user-email" />
                  </div>
                  <div>
                    <Label>Contraseña</Label>
                    <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={6} data-testid="input-new-user-password" />
                  </div>
                  {needsMunicipio && (
                    <div>
                      <Label>Municipio</Label>
                      <Select value={String(newUser.municipio_clave || "")} onValueChange={(v) => setNewUser({ ...newUser, municipio_clave: v })}>
                        <SelectTrigger data-testid="select-user-municipio"><SelectValue placeholder="Selecciona municipio" /></SelectTrigger>
                        <SelectContent>
                          {municipios.map((m) => (
                            <SelectItem key={m.municipio_clave} value={String(m.municipio_clave)}>{m.municipio_nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {needsRegion && (
                    <>
                      <div>
                        <Label>Tipo de región</Label>
                        <Select value={newUser.region_type} onValueChange={(v) => setNewUser({ ...newUser, region_type: v })}>
                          <SelectTrigger data-testid="select-region-type"><SelectValue placeholder="zona o colonia" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="zona">Zona / Región</SelectItem>
                            <SelectItem value="colonia">Colonia</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Nombre de la región / colonia</Label>
                        <Input value={newUser.region_name} onChange={(e) => setNewUser({ ...newUser, region_name: e.target.value })} required data-testid="input-region-name" />
                      </div>
                    </>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                    <Button type="submit" className="btn-primary" data-testid="submit-new-user">Crear</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {users.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Sin usuarios.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {users.map((u) => (
                <div key={u.id} className="px-6 py-4 flex items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge variant="outline" className="uppercase tracking-wider text-[10px]">{ROLE_LABELS[u.role]}</Badge>
                      {u.region_name && (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">{u.region_type}: {u.region_name}</Badge>
                      )}
                      {u.municipio_nombre && (
                        <Badge variant="outline">{u.municipio_nombre}</Badge>
                      )}
                    </div>
                  </div>
                  {u.role !== "super_admin" && u.id !== s.responsible_admin_id && (
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => removeUser(u.id)} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function scopeTypeLabel(s) {
  const st = s?.scope_type || s?.scope;
  if (st === "distrital_federal") return `DISTRITAL FEDERAL${s?.distrito_clave ? ` · Distrito ${s.distrito_clave}` : ""}`;
  if (st === "distrital_local") return `DISTRITAL LOCAL${s?.distrito_clave ? ` · Distrito ${s.distrito_clave}` : ""}`;
  if (st === "municipal") return "MUNICIPAL";
  if (st === "estatal") return "ESTATAL";
  return (st || "").toUpperCase();
}

const MODULES_META = [
  { key: "viabilidad",   num: "M1", label: "Viabilidad",                icon: Sparkles,   accent: "from-blue-500 to-indigo-600",   locked: true,  hint: "Módulo base · siempre activo" },
  { key: "estructura",   num: "M2", label: "Construcción de estructura", icon: Building2, accent: "from-slate-500 to-slate-700",    locked: true,  hint: "Módulo base · siempre activo" },
  { key: "fidelizacion", num: "M3", label: "Fidelización",              icon: Heart,      accent: "from-rose-500 to-red-600",       locked: false, hint: "Retención y activación de simpatizantes" },
  { key: "dispersion",   num: "M4", label: "Dispersión",                icon: Rocket,     accent: "from-amber-500 to-orange-600",   locked: false, hint: "Cobertura territorial capilarizada" },
  { key: "dia_d",        num: "M5", label: "Día D",                     icon: Flag,       accent: "from-emerald-500 to-teal-600",   locked: false, hint: "Movilización · defensa del voto · cómputo" },
];

function ModulesToggleSection({ subaccount, onToggle }) {
  const modules = subaccount?.modules || {
    viabilidad: true, estructura: true, fidelizacion: false, dispersion: false, dia_d: false,
  };
  return (
    <section className="surface-card mt-10 overflow-hidden" data-testid="modules-toggle-section">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="font-display font-bold text-lg text-slate-900">Módulos NOCTUA 360</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Habilita o deshabilita módulos por subcuenta. M1 y M2 son base y no se pueden desactivar.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 p-6">
        {MODULES_META.map((m) => {
          const Icon = m.icon;
          const active = !!modules[m.key];
          const disabled = m.locked;
          return (
            <div
              key={m.key}
              className={`relative rounded-lg border p-4 overflow-hidden ${
                active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200"
              }`}
              data-testid={`module-toggle-${m.key}`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.accent} ${active ? "opacity-100" : "opacity-30"}`} />
              <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br ${m.accent} ${active ? "" : "opacity-40 grayscale"}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                {disabled && <Lock className="w-4 h-4 text-slate-400" title="Módulo base" />}
              </div>
              <div className="mt-3 text-[10px] tracking-widest text-slate-500 font-semibold">{m.num}</div>
              <div className={`font-display font-bold text-sm mt-0.5 ${active ? "text-slate-900" : "text-slate-500"}`}>{m.label}</div>
              <div className="text-xs text-slate-500 mt-1 leading-snug min-h-[32px]">{m.hint}</div>
              <label className={`mt-3 flex items-center justify-between gap-2 pt-2 border-t border-slate-100 ${disabled ? "opacity-60" : ""}`}>
                <span className="text-xs text-slate-600 font-medium">
                  {active ? "Habilitado" : "Deshabilitado"}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  disabled={disabled}
                  onClick={() => !disabled && onToggle(m.key, !active)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    active ? "bg-blue-600" : "bg-slate-300"
                  } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                  data-testid={`module-switch-${m.key}`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      active ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
}
